import '../assets/scss/mobi.scss'

import * as React from 'react'
import { createRoot } from 'react-dom/client'
import * as analyticsDataLayer from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import * as rwwaRxStateMachineDevTools from '@core/Utils/rwwa-rx-state-machine-dev-tools'

import { queryClient } from '@core/Data/ReactQuery'
import { serverConfigQuery } from '@core/Data/ServerConfig/query'
import {
  startDeepLinkingListeners,
  startUserLoginListeners,
  startLocationServiceListeners,
  startPushNotificationsAuthStatus,
  startMetadataStatusUpdate,
  startRaceKeyNotification,
  startWebviewIsAliveListener,
} from '@core/NativeServices'
import '@classic/AppUtils/Framework/CustomBindings/React'
import { registerUserAccountSideEffects } from '@core/State/UserAccount/side-effects'
import { initializeLaunchDarklyServiceWithConfig } from '@core/State/FeatureFlags/LaunchDarklyService'
import '@core/State/PushData'
import { iotSubscribeTopics } from '@mobi/utils/awsiot'
import { getGlobalPushSubscriptions } from '@mobi/utils/awsiot/topics'
import App from '@core/App'
import { init as initWebNativeComms } from '@mobi/web-native-comms/web'
import { refreshLogonState } from '@core/Areas/Login/helpers/refreshLogonState'
import { initializeSentry } from '@core/Services/Sentry'
import { setHostHelpers } from '@mobi/account/Utils/hostHelpers'
import { appHistory } from '@core/Utils/routing/history'
import { getIsAuthenticated } from '@core/Areas/Login/helpers/api'
import { startVenueModeService } from '@core/Areas/VenueMode/Service/venueModeService'

window.AnalyticsDataLayer = analyticsDataLayer

const [isAuthenticated, config] = await Promise.all([
  getIsAuthenticated(),
  queryClient.fetchQuery(serverConfigQuery),
])

const { enableSentryIo, sentryIoUrl, serverEnvironment } = config

if (enableSentryIo) {
  initializeSentry(sentryIoUrl, serverEnvironment)
}

const { launchDarklyClientID } = config
const { accountNumber } = isAuthenticated

await initializeLaunchDarklyServiceWithConfig(launchDarklyClientID, accountNumber)

setHostHelpers({ history: appHistory })
initWebNativeComms()

startDeepLinkingListeners()
startUserLoginListeners()
startLocationServiceListeners()
startPushNotificationsAuthStatus()
startMetadataStatusUpdate()
startRaceKeyNotification()
startVenueModeService()
startWebviewIsAliveListener()

// Register driver side effects here
registerUserAccountSideEffects()

await refreshLogonState(isAuthenticated)

// Render <App />
const container = document.getElementById('js-react-app')
if (container) {
  const root = createRoot(container)
  root.render(React.createElement(App, null))
}

// Do global push subscriptions
iotSubscribeTopics(getGlobalPushSubscriptions())

// Set up DEV tools
if (!PRODUCTION) {
  rwwaRxStateMachineDevTools.start()
  // @ts-expect-error DEV only global
  if (module.hot) module.hot.accept()
}
